import React from "react";
import { PromptLibraryItem } from "../promptLibrary.types";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { clsx } from "clsx";
import { ChevronDown, ChevronUp, MessageSquareIcon } from "lucide-react";
import * as analytics from "@/services/analytics";

interface Props {
  item: PromptLibraryItem;
  userName?: string;
  onSendToChat: (prompt: string) => void;
}

export const PromptLibraryCard: React.FC<Props> = ({ item, userName, onSendToChat }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div>
      <div className="rounded-lg border border-input bg-white/80 p-3 transition-all hover:border-desert-sand-400 hover:bg-ivory-mist-100">
        <div className="flex items-start justify-between">
          <h4 className="text-sm font-medium text-foreground">{item.name}</h4>
          {userName && <span className="text-xs text-muted-foreground">{userName}</span>}
        </div>
        <div className="relative">
          <div
            className={clsx(
              "mt-1.5 overflow-hidden text-sm text-muted-foreground transition-all duration-300",
              isExpanded ? "max-h-[500px]" : "max-h-[60px]",
            )}
          >
            <MarkdownRenderer content={item.prompt} evidences={[]} processCitations={true} />
          </div>
          <div
            className={clsx(
              "pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white/80 to-transparent transition-opacity duration-300",
              isExpanded ? "opacity-0" : "opacity-100",
            )}
          />
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? (
            <>
              <ChevronUp size={14} />
              Show less
            </>
          ) : (
            <>
              <ChevronDown size={14} />
              Show more
            </>
          )}
        </button>
        {item.notes && (
          <p className="mt-3 text-xs text-muted-foreground">
            <span className="font-semibold text-amber-700">Notes:</span> {item.notes}
          </p>
        )}
      </div>
      <div className="mt-1.5 flex justify-end">
        <button
          className="flex rounded-md bg-[#585451] p-1 text-xs text-white"
          onClick={() => {
            analytics.promptLibrarySendToChat(item.name);
            onSendToChat(item.prompt);
          }}
        >
          <MessageSquareIcon size={14} className="mr-1" />
          Send to chat
        </button>
      </div>
    </div>
  );
};
